import { useMutation, useQueryClient } from '@tanstack/react-query'
import type { UnknownOutputParams } from 'expo-router'

import { useEmailHash } from '@/src/features/auth/services/useEmailHash'
import { prayApi } from '@/src/features/pray/api/prayApi'
import { PRAY_OBJECT_TYPE } from '@/src/features/pray/models/prayObjectType'

import { COMMUNITY_LIFETIME } from '../../community/models/community.constants'
import { getActiveJourneySubscriptionsOptions } from '../../community/services/useActiveJourneySubscriptionsQuery'
import { getQueryCommunitySubscriptionOptions } from '../../community/services/useCommunitySubscriptionsQuery'
import { getEverlastingSubscriptionsOptions } from '../../community/services/useEverlastingSubscriptionsQuery'
import { getPublicationsOfCommunityOptions } from '../../community/services/usePublicationsOfCommunityQuery'
import { invalidateSubscriptionQueries } from '../../community/utils/invalidateSubscriptionQueries'
import { publicationOptions } from './options/publicationOptions'

type TUsePublicationPrayMutationProps = {
  id: number
  searchParams: UnknownOutputParams
  communityLifetime: COMMUNITY_LIFETIME | undefined
  communityId: number | undefined
  hasJoinedCommunity: boolean | undefined
}

export const usePublicationPrayMutation = ({
  id,
  searchParams,
  communityLifetime,
  communityId,
  hasJoinedCommunity,
}: TUsePublicationPrayMutationProps) => {
  const emailHash = useEmailHash(searchParams)
  const queryClient = useQueryClient()

  return useMutation({
    mutationFn: () => prayApi.createPrayer({ objectId: id, objectType: PRAY_OBJECT_TYPE.PUBLICATION, emailHash }),
    onSuccess: () => {
      queryClient.setQueryData(publicationOptions(id).queryKey, (data) => data && { ...data, hasPrayed: true })
      if (communityId) {
        if (communityLifetime)
          queryClient.invalidateQueries(
            getQueryCommunitySubscriptionOptions({ id: communityId, lifetime: communityLifetime }),
          )
        invalidateSubscriptionQueries(queryClient, {
          id: communityId,
          lifetime: communityLifetime,
          hasJoined: !!hasJoinedCommunity,
        })
      }
      if (hasJoinedCommunity) {
        queryClient.invalidateQueries(
          communityLifetime === COMMUNITY_LIFETIME.EVERLASTING
            ? getEverlastingSubscriptionsOptions()
            : getActiveJourneySubscriptionsOptions(),
        )
      }
      if (communityLifetime && communityId) {
        queryClient.setQueryData(
          getPublicationsOfCommunityOptions({ id: communityId, type: communityLifetime, enabled: true }).queryKey,
          (data) =>
            data && {
              ...data,
              pages: data.pages.map((page) =>
                page.map((publication) => (publication.id === id ? { ...publication, hasPrayed: true } : publication)),
              ),
            },
        )
      }
    },
  })
}
